import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { LocationOn, Phone, Email, Language, WhatsApp } from "@mui/icons-material";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleEmailSend = () => {
    const subject = encodeURIComponent("Inquiry from Namami India Website");
    const body = encodeURIComponent(
      `Hello Namami India,\n\nMy name is ${formData.name}.\nEmail: ${formData.email}\n\nMessage:\n${formData.message}\n\nRegards,\n${formData.name}`
    );
    window.location.href = `mailto:info@namamiindia.co.in?subject=${subject}&body=${body}`;
  };

  const handleWhatsAppSend = () => {
    const number = "+919692783839";
    const message = encodeURIComponent(
      `Hello Namami India,\n\nMy name is ${formData.name}.\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    );
    window.open(`https://wa.me/${number}?text=${message}`, "_blank");
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
           minHeight: "100vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Container
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          justifyContent: "center",
          gap: { xs: 5, md: 8 },
        }}
      >
        {/* === Contact Info === */}
        <Card
          sx={{
            flex: 1,
            p: { xs: 3, sm: 4 },
            background: "rgba(255,255,255,0.8)",
            borderRadius: 4,
            backdropFilter: "blur(8px)",
            boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: "0 12px 35px rgba(0,0,0,0.12)",
            },
          }}
        >
          <Typography
            variant="h4"
            fontWeight={800}
            mb={3}
            sx={{
              background: "linear-gradient(90deg, #1565c0, #004ba0)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: { xs: "center", md: "left" },
            }}
          >
            Contact Us
          </Typography>

          <Box display="flex" alignItems="flex-start" mb={2}>
            <LocationOn sx={{ color: "primary.main", mr: 1.5, mt: 0.5 }} />
            <Typography color="text.secondary" lineHeight={1.7}>
              Behind Gowrishankar English Medium School, Plot No - 239, Rudra Vihar, 
              Sishupal Garh, Dhauli, Bhubaneswar, Odisha – 751002
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" mb={1.5}>
            <Phone sx={{ color: "primary.main", mr: 1.5 }} />
            <Typography color="text.secondary">
              +91 96927 83839, +91 89176 29368, +91 98401 10545
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" mb={1.5}>
            <Email sx={{ color: "primary.main", mr: 1.5 }} />
            <Typography color="text.secondary">info@namamiindia.co.in</Typography>
          </Box>

          <Box display="flex" alignItems="center" mb={3}>
            <Language sx={{ color: "primary.main", mr: 1.5 }} />
            <Typography color="text.secondary">www.namamiindia.co.in</Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Quick Contact Buttons */}
          <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Email />}
              onClick={handleEmailSend}
              sx={{
                textTransform: "none",
                borderRadius: 3,
                px: 3,
                flex: 1,
              }}
            >
              Send Email
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<WhatsApp />}
              onClick={handleWhatsAppSend}
              sx={{
                textTransform: "none",
                borderRadius: 3,
                px: 3,
                flex: 1,
                borderWidth: 2,
                "&:hover": {
                  borderWidth: 2,
                  backgroundColor: "rgba(76,175,80,0.1)",
                },
              }}
            >
              WhatsApp Us
            </Button>
          </Box>
        </Card>

        {/* === Message Form === */}
        <Card
          sx={{
            flex: 1,
            p: { xs: 3, sm: 4 },
            background: "rgba(255,255,255,0.85)",
            borderRadius: 4,
            backdropFilter: "blur(8px)",
            boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: "0 12px 35px rgba(0,0,0,0.12)",
            },
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Typography
              variant="h5"
              fontWeight={700}
              mb={2}
              sx={{
                color: "primary.main",
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Send us a Message
            </Typography>
            <TextField
              fullWidth
              label="Your Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Your Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Your Message"
              name="message"
              multiline
              rows={4}
              value={formData.message}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
            />

            <Box
              display="flex"
              gap={2}
              mt={3}
              flexWrap="wrap"
              justifyContent={{ xs: "center", md: "flex-start" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleEmailSend}
                sx={{
                  textTransform: "none",
                  borderRadius: 3,
                  px: 4,
                  py: 1.2,
                  boxShadow: "0 3px 10px rgba(21,101,192,0.3)",
                }}
              >
                Send via Email
              </Button>
              <Button
                variant="outlined"
                color="success"
                onClick={handleWhatsAppSend}
                startIcon={<WhatsApp />}
                sx={{
                  textTransform: "none",
                  borderRadius: 3,
                  px: 4,
                  py: 1.2,
                  borderWidth: 2,
                  "&:hover": {
                    borderWidth: 2,
                    backgroundColor: "rgba(76,175,80,0.1)",
                  },
                }}
              >
                Send via WhatsApp
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
